import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { Button, TableLink } from "../../atoms";
import { rawHex } from "../../../design-system/tokens/colors";
import { spacing } from "../../../design-system/tokens/spacing";
import { typographyMixin } from "../../../design-system/tokens/typography";
import {
  CollapsibleSection,
  DataTable,
  Pagination,
  type DataTableColumn,
} from "../../molecules";
import type { ConnectedDataset } from "../../../pages/Connectors/api";
import { buildCsvPreviewColumns } from "../../../services/csv/csvConnectionDetail.utils";
import {
  isConnectedDatasetSectionFilePreviewProps,
  type ConnectedDatasetSectionDatasetProps,
  type ConnectedDatasetSectionFilePreviewProps,
  type ConnectedDatasetSectionProps,
} from "./ConnectedDatasetSection.types";

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [5, 10, 25] as const;

const DatasetTableLink = styled(Link)`
  ${typographyMixin("body", 3)}
  color: ${rawHex.primary[500]};
  text-decoration: none;

  &:hover {
    color: ${rawHex.primary[600]};
    text-decoration: none;
  }

  &:focus-visible {
    outline: 2px solid ${rawHex.primary[500]};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const TableCard = styled.div`
  border: 1px solid ${rawHex.stroke[200]};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  overflow: hidden;
  background-color: ${rawHex.structural.white};
`;

const HeaderActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[4]};
`;

const buildColumns = (
  connectionId: string | undefined,
  onScheduleRefresh: (table: string, assetId: string) => void,
  isActionDisabled: boolean,
): Array<DataTableColumn<ConnectedDataset>> => [
  {
    key: "table",
    header: "TABLE",
    bodyVariant: 3,
    emphasis: "high",
    render: (row) =>
      connectionId && !isActionDisabled ? (
        <DatasetTableLink
          to={`/data-connectors/${connectionId}/tables/${encodeURIComponent(row.schema || "_")}/${encodeURIComponent(row.table)}?asset=${encodeURIComponent(row.assetId)}`}
        >
          {row.table.toLowerCase()}
        </DatasetTableLink>
      ) : (
        row.table.toLowerCase()
      ),
  },
  {
    key: "schema",
    header: "SCHEMA",
    bodyVariant: 4,
    emphasis: "high",
    render: (row) => row.schema || "—",
  },
  {
    key: "rows",
    header: "ROWS",
    bodyVariant: 4,
    emphasis: "medium",
    render: (row) => (row.rowCount != null ? `${row.rowCount.toLocaleString()} rows` : "—"),
  },
  {
    key: "columns",
    header: "COLUMNS",
    bodyVariant: 4,
    emphasis: "medium",
    render: (row) => (row.columnCount != null ? `${row.columnCount} columns` : "—"),
  },
  {
    key: "action",
    header: "",
    width: "160px",
    align: "right",
    bodyVariant: 3,
    emphasis: "interactive",
    render: (row) => (
      <TableLink
        disabled={isActionDisabled}
        onClick={() => onScheduleRefresh(row.table, row.assetId)}
      >
        Schedule refresh
      </TableLink>
    ),
  },
];

const ConnectedFilePreviewSection: React.FC<
  ConnectedDatasetSectionFilePreviewProps
> = ({
  filePreview,
  expanded,
  onToggle,
  isActionDisabled = false,
  className,
}) => {
  const { columns, rows, totalRowCount, onReupload, onDownload } = filePreview;
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const pageCount = Math.max(1, Math.ceil(totalRowCount / pageSize));
  const pageRows = useMemo(
    () => rows.slice((page - 1) * pageSize, page * pageSize),
    [rows, page, pageSize],
  );
  const tableColumns = useMemo(
    () => buildCsvPreviewColumns(columns),
    [columns],
  );
  const rowKey = columns[0] ?? "row";

  return (
    <CollapsibleSection
      className={className}
      title="Preview"
      expanded={expanded}
      onToggle={onToggle}
      headerActions={
        <HeaderActionsRow>
          <Button
            variant="link-pill"
            size="xs"
            onClick={(event) => {
              event.stopPropagation();
              if (isActionDisabled) return;
              onReupload();
            }}
            disabled={isActionDisabled}
            data-testid="csv-reupload-button"
          >
            Reupload
          </Button>
          <Button
            variant="link-pill"
            size="xs"
            onClick={(event) => {
              event.stopPropagation();
              if (isActionDisabled) return;
              onDownload();
            }}
            disabled={isActionDisabled}
            data-testid="csv-download-button"
          >
            Download
          </Button>
        </HeaderActionsRow>
      }
    >
      <TableCard>
        <DataTable
          chromeless
          columns={tableColumns}
          rows={pageRows}
          rowKey={(row) => String(row[rowKey] ?? "")}
        />
        <Pagination
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          totalCount={totalRowCount}
          onPageChange={setPage}
          pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
          onPageSizeChange={(nextPageSize) => {
            setPageSize(nextPageSize);
            setPage(1);
          }}
        />
      </TableCard>
    </CollapsibleSection>
  );
};

const ConnectedDatasetTableSection: React.FC<
  ConnectedDatasetSectionDatasetProps
> = ({
  connectionId,
  datasets,
  expanded,
  onToggle,
  onEditDataset,
  onScheduleRefresh,
  onDatasetPaginationChange,
  showEditDataset = true,
  isActionDisabled = false,
  className,
}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const totalCount = datasets.length;
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));
  const pageRows = useMemo(
    () => datasets.slice((page - 1) * pageSize, page * pageSize),
    [datasets, page, pageSize],
  );
  const columns = useMemo(
    () => buildColumns(connectionId, onScheduleRefresh, isActionDisabled),
    [connectionId, onScheduleRefresh, isActionDisabled],
  );

  if (totalCount === 0) {
    return null;
  }

  return (
    <CollapsibleSection
      className={className}
      title="Connected dataset"
      expanded={expanded}
      onToggle={onToggle}
      headerActions={
        showEditDataset ? (
          <Button
            variant="link-pill"
            size="xs"
            onClick={(event) => {
              event.stopPropagation();
              if (isActionDisabled) return;
              onEditDataset();
            }}
            disabled={isActionDisabled}
          >
            Edit dataset
          </Button>
        ) : undefined
      }
    >
      <TableCard>
        <DataTable
          chromeless
          columns={columns}
          rows={pageRows}
          rowKey={(row) => `${row.schema}.${row.table}`}
        />
        <Pagination
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={(nextPage) => {
            setPage(nextPage);
            onDatasetPaginationChange?.();
          }}
          pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
          onPageSizeChange={(nextPageSize) => {
            setPageSize(nextPageSize);
            setPage(1);
          }}
        />
      </TableCard>
    </CollapsibleSection>
  );
};

export const ConnectedDatasetSection: React.FC<
  ConnectedDatasetSectionProps
> = (props) => {
  if (isConnectedDatasetSectionFilePreviewProps(props)) {
    return <ConnectedFilePreviewSection {...props} />;
  }

  return <ConnectedDatasetTableSection {...props} />;
};

export default ConnectedDatasetSection;
